import { useEffect, useState, useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import flattenPaginatedData from '../helpers/flattenPaginatedData';
import useIntersectionObserverPagination from './useIntersectionObserverPagination';

export default function usePaginatedDropdownOptions({
  dataName,
  getDataHandler,
  searchTerm,
  labelName = 'name',
  isUserList,
  isJobPostsPicker,
  isCompanyList,
  isExternalUserList,
  fetchHanlderOptions,
}) {
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const timer = useRef();

  const { data, fetchNextPage, isFetching, refetch } = useInfiniteQuery({
    queryKey: [dataName, debouncedSearchTerm, fetchHanlderOptions],
    queryFn: ({ pageParam = 1 }) => {
      return getDataHandler?.({
        // orderBy: activeSortOption.value || 'id',
        term: debouncedSearchTerm,
        pageNumber: pageParam,
        ...(fetchHanlderOptions || {}),
      });
    },
    keepPreviousData: true,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextPage;
    },
    enabled: !!getDataHandler,
  });

  useEffect(() => {
    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
  }, [searchTerm]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchTerm, refetch]);

  const { lastListElementRef } = useIntersectionObserverPagination({
    isFetching,
    fetchNextPage,
  });

  const options =
    flattenPaginatedData(data)?.map?.((value) => {
      let optionLabel;
      if (isUserList) {
        optionLabel = `${value?.userProfile?.firstName} ${
          value?.userProfile?.lastName
        } ${
          value?.userProfile?.designation
            ? `(${value?.userProfile?.designation})`
            : ''
        } - ${value?.userName}`;
      } else if (isCompanyList) {
        optionLabel = `${value?.name}`;
      } else if (isExternalUserList) {
        optionLabel = `${value?.firstName} ${value?.lastName} ${
          value?.designation ? `(${value?.designation})` : ''
        } ${value?.email}`;
      } else if (isJobPostsPicker) {
        const companyName = value?.company?.name || value?.companyName || 'N/A';
        optionLabel = `${value?.jobTitle} - ${companyName}`;
      } else {
        optionLabel = value?.[labelName];
      }

      return {
        label: optionLabel,
        value: value?.id,
        originalData: value,
      };
    }) || [];

  return {
    options,
    lastListElementRef,
    isFetching,
  };
}
